/**
 * Regras de persistência na finalização (edição vs atendimento novo).
 *
 * Alinhamento com TabsAssinatura / DocumentVisualization:
 * - Pendente de assinatura = !sign (exceto questionário)
 * - Ao finalizar com PDF pendente → sempre postar DocumentReference
 * - basedOn do DocumentReference = TODOS os itens do PDF pendente
 *   (MedicationRequest/ServiceRequest já existentes e/ou recém-POSTados)
 */

/** Conteúdo alterado na edição / reemissão (flag já usada no fluxo de refact). */
export function isInterventionContentAltered(el: any): boolean {
  return !!el?.refact;
}

/**
 * Pendente de assinatura na TabsAssinatura (mesma regra da lista "Assinaturas pendentes").
 * Questionários não geram DocumentReference de assinatura.
 */
export function isPendingSignatureIntervention(el: any): boolean {
  return !!el && !el?.sign && el?.type !== "questionnaire";
}

/**
 * Sempre cria DocumentReference no finish para item pendente de assinatura
 * (TabsAssinatura / DocumentVisualization). basedOn usa ids já no FHIR e/ou POSTs novos.
 */
export function shouldPostInterventionDocument(el: any): boolean {
  return isPendingSignatureIntervention(el);
}

/**
 * Upsert de MedicationRequest (POST se novo / PUT se já tem id):
 * - item novo (sem medicationRequestId) → POST no Bundle,
 * - edição com conteúdo alterado (`refact`) → PUT MedicationRequest/{id}.
 * Órfão não alterado: não upsert — DocumentReference usa o id existente no basedOn.
 */
export function shouldUpsertMedication(el: any): boolean {
  if (!isPendingSignatureIntervention(el)) return false;
  if (!el?.medicationRequestId) {
    return true;
  }
  return isInterventionContentAltered(el);
}

/**
 * Upsert de ServiceRequest (POST se novo / PUT se já tem id) — mesma regra do medicamento.
 */
export function shouldUpsertService(el: any): boolean {
  if (!isPendingSignatureIntervention(el)) return false;
  if (!el?.serviceRequestId) {
    return true;
  }
  return isInterventionContentAltered(el);
}

/** Precisa de alguma persistência (recurso e/ou DocumentReference). */
export function needsInterventionPersist(el: any): boolean {
  return (
    shouldUpsertMedication(el) ||
    shouldUpsertService(el) ||
    shouldPostInterventionDocument(el)
  );
}

/**
 * Extrai o payload base64 (sem data-URL prefix) de uma chave do estado base64
 * gerado pelo DocumentVisualization (ex.: "simple-medication").
 */
export function getDocBase64Payload(
  base64List: any[] | undefined,
  key: string
): string | undefined {
  return base64List
    ?.find((el: any) => el?.[key])
    ?.[key]?.base64?.split(",")?.[1];
}

/**
 * Aguarda as chaves de PDF ficarem prontas no snapshot do base64.
 * Necessário porque TabsAssinatura/DocumentVisualization geram o PDF de forma assíncrona.
 */
export async function waitForDocBase64Keys(
  getSnapshot: () => any[] | undefined,
  keys: string[],
  options?: { timeoutMs?: number; intervalMs?: number }
): Promise<any[] | undefined> {
  if (!keys.length) return getSnapshot();

  const timeoutMs = options?.timeoutMs ?? 20000;
  const intervalMs = options?.intervalMs ?? 250;
  const start = Date.now();

  while (Date.now() - start < timeoutMs) {
    const snapshot = getSnapshot();
    const allReady = keys.every((key) => !!getDocBase64Payload(snapshot, key));
    if (allReady) return snapshot;
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  return getSnapshot();
}

/**
 * Normaliza location do Bundle transaction (relativo ou URL absoluta)
 * para `ResourceType/{id}`, removendo `/_history/...`.
 */
export function normalizeBundleLocationToRef(
  location: string | undefined,
  resourceType: string
): string | undefined {
  if (!location) return undefined;

  const withoutHistory = location.split("/_history")[0];
  const parts = withoutHistory.split("/").filter(Boolean);
  const typeIdx = parts.lastIndexOf(resourceType);

  if (typeIdx >= 0 && parts[typeIdx + 1]) {
    return `${resourceType}/${parts[typeIdx + 1]}`;
  }

  return withoutHistory;
}

/**
 * Junta N referências em blocos <fhir:basedOn> (1 DocumentReference → N recursos).
 * Remove duplicatas mantendo a ordem.
 */
export function joinBasedOnXmlRefs(refs: Array<string | undefined | null>): string {
  const seen = new Set<string>();
  return (
    refs
      .map((ref) => (typeof ref === "string" ? ref.trim() : ""))
      .filter((ref) => {
        if (!ref || seen.has(ref)) return false;
        seen.add(ref);
        return true;
      })
      .map(
        (ref) => `<fhir:basedOn>
            <fhir:reference value="${ref}"/>
            </fhir:basedOn>`
      )
      .join("") || ""
  );
}

/**
 * Resolve referência FHIR de um item do PDF pendente:
 * - recém-POSTado → location da response
 * - PUT/já existente → MedicationRequest/{id} ou ServiceRequest/{id}
 *   (location pode faltar no PUT; fallback pelo id do item)
 */
function resolveResourceRef(
  item: any,
  postedItems: any[] | undefined,
  responseEntries: any[] | undefined,
  resourceType: "MedicationRequest" | "ServiceRequest"
): string | undefined {
  const idKey =
    resourceType === "MedicationRequest"
      ? "medicationRequestId"
      : "serviceRequestId";

  let postedIndex = postedItems?.indexOf(item) ?? -1;

  if (postedIndex < 0 && item?.[idKey] && postedItems?.length) {
    postedIndex = postedItems.findIndex(
      (p) => p?.[idKey] && p[idKey] === item[idKey]
    );
  }

  if (postedIndex >= 0) {
    const locationRef = normalizeBundleLocationToRef(
      responseEntries?.[postedIndex]?.response?.location,
      resourceType
    );
    if (locationRef) return locationRef;
  }

  const existingId = item?.[idKey];
  if (existingId) return `${resourceType}/${existingId}`;
  return undefined;
}

/**
 * Monta basedOn XML para o lote informado (índice alinhado à response do POST).
 * 1 DocumentReference → N basedOn (um por item).
 */
export function buildBasedOnXmlForResources(
  items: any[],
  responseEntries: any[] | undefined,
  resourceType: "MedicationRequest" | "ServiceRequest",
  filterFn?: (item: any) => boolean
): string {
  const list = (filterFn ? items?.filter(filterFn) : items) || [];
  const refs = list.map((item: any) =>
    resolveResourceRef(item, items, responseEntries, resourceType)
  );
  return joinBasedOnXmlRefs(refs);
}

/**
 * basedOn do DocumentReference do PDF pendente:
 * inclui TODOS os itens do grupo (já postados e/ou novos no lote).
 */
export function buildBasedOnXmlForDocumentItems(
  documentItems: any[],
  postedItems: any[] | undefined,
  responseEntries: any[] | undefined,
  resourceType: "MedicationRequest" | "ServiceRequest"
): string {
  const refs = (documentItems || []).map((item: any) =>
    resolveResourceRef(item, postedItems, responseEntries, resourceType)
  );
  return joinBasedOnXmlRefs(refs);
}

/**
 * Monta basedOn XML apenas para itens que ainda vão receber DocumentReference,
 * assumindo que `items` === lote POSTado (índices alinhados à response).
 */
export function buildBasedOnXmlForNewDocuments(
  items: any[],
  responseEntries: any[] | undefined,
  resourceType: "MedicationRequest" | "ServiceRequest"
): string {
  return buildBasedOnXmlForResources(
    items,
    responseEntries,
    resourceType,
    shouldPostInterventionDocument
  );
}
